import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import { filterBySearch, sortData } from '@/components/data-table/data-table.helper'
import type { DataTablePagination, SortOrder } from '@/components/data-table/data-table.interface'

import { promptTemplateSearchKeyList } from './prompt-template-table.helper'

interface UsePromptTemplateTableStateProps {
  itemList: PromptTemplate[]
  total: number
}

interface UsePromptTemplateTableStateReturn {
  paginatedData: PromptTemplate[]
  processedTotalCount: number
  searchValue: string
  sortBy: string
  sortOrder: SortOrder
  pagination: DataTablePagination
  setSearchValue: (value: string) => void
  handleSortChange: (sortBy: string, sortOrder: SortOrder) => void
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
}

export const usePromptTemplateTableState = ({
  itemList,
  total,
}: UsePromptTemplateTableStateProps): UsePromptTemplateTableStateReturn => {
  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState(searchParams.get('q') ?? '')
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [pagination, setPagination] = useState<DataTablePagination>({
    page: 1,
    pageSize: 25,
    totalCount: total,
  })

  const processedData = useMemo(() => {
    const searched = filterBySearch(itemList, searchValue, promptTemplateSearchKeyList)
    return sortData(searched, sortBy, sortOrder)
  }, [itemList, searchValue, sortBy, sortOrder])

  const handleSortChange = useCallback((newSortBy: string, newSortOrder: SortOrder) => {
    setSortBy(newSortBy)
    setSortOrder(newSortOrder)
  }, [])

  const handlePageChange = useCallback(
    (page: number) => setPagination((prev) => ({ ...prev, page })),
    []
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => setPagination((prev) => ({ ...prev, pageSize, page: 1 })),
    []
  )

  return {
    paginatedData: processedData,
    processedTotalCount: searchValue ? processedData.length : total,
    searchValue,
    sortBy,
    sortOrder,
    pagination: { ...pagination, totalCount: searchValue ? processedData.length : total },
    setSearchValue,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
  }
}
